export enum PermissionModule {
  ENQUIRY = "enquiry",
  WORK_ORDER = "work_order",
  PETTY_WORK = "petty_work",
  DASHBOARD = "dashboard",
  SITE_VISIT = "site_visit",
  AMC_VISIT = "amc_visit",
  AMC = "amc",
  EMPLOYEE_ATTENDANCE = "employee_attendance",
  EMPLOYEE = "employee",
  EMPLOYEE_SALARY_STRUCTURE = "employee_salary_structure",
  EMPLOYMENT_CATEGORY = "employment_category",
  LEAVE_APPLICATION = "leave_application",
  LEAVE_TYPE = "leave_type",
  LEAVE_POLICY = "leave_policy",
  USER = "user",
  ROLES_AND_PERMISSIONS = "roles_and_permissions",
  CLIENT = "client",
  QUOTATION = "quotation",
  PRODUCT = "product",
  CLIENT_ADVANCE = "client_advance",
  CLIENT_ADVANCE_SETTLEMENT = "client_advance_settlement",
  EMPLOYEE_ADVANCE = "employee_advance",
  EMPLOYEE_ADVANCE_SETTLEMENT = "employee_advance_settlement",
  PAYROLL = "payroll",
  MATERIAL_REQUEST = "material_request",
  STOCK_LOCATION = "stock_location",
  STOCK_MOVEMENT = "stock_movement",
  MATERIAL_ISSUE = "material_issue",
  INVOICE = "invoice",
  PAYMENT = "payment",
  SUPPLIER = "supplier",
  PURCHASE_ORDER = "purchase_order",
  GOODS_RECEIPT = "goods_receipt",
  WORK_ASSIGNMENT = "work_assignment",
  DEPARTMENT = "department",
  DESIGNATION = "designation",
  SETTINGS = "settings",
  HOLIDAY = "holiday",
  PAYMENT_EXPECTATION = "payment_expectation",
  ACTIVITY_LOG = "activity_log",
}

export enum PermissionAction {
  CREATE = "create",
  READ = "read",
  UPDATE = "update",
  DELETE = "delete",

  APPROVE = "approve",
  /** AMC DRAFT -> READY_FOR_ACTIVATION */
  READY_FOR_ACTIVATION = "ready_for_activation",
  CONFIRM = "confirm",
  CANCEL = "cancel",
  CLOSE = "close",
  TERMINATE = "terminate",
  REJECT = "reject",
  PROCESS = "process",
  SEND = "send",
  ACCEPT = "accept",
  /** Work order, petty work, site visit, AMC visit state transitions */
  SCHEDULE = "schedule",
  START = "start",
  HOLD = "hold",
  RESUME = "resume",
  COMPLETE = "complete",
  SUBMIT_BILLING = "submit_billing",
  /** Client advance DRAFT → PROCESSED (attach the Payment). */
  RECEIVE = "receive",
  /** Client advance PROCESSED → REFUNDED (full remaining balance only). */
  REFUND = "refund",
  /** Employee advance DRAFT → PROCESSED (attach the Payment). */
  DISBURSE = "disburse",
  /** Payroll run */
  FINALIZE = "finalize",
  // Assignments
  ASSIGN = "assign",
  REASSIGN = "reassign",
  // Attendance
  CLOCK_IN = "clock_in",
  CLOCK_OUT = "clock_out",
  // Scoped read
  READ_BASIC = "read_basic",
}

export const buildPermission = (
  module: PermissionModule,
  action: PermissionAction,
) => {
  return `${module}.${action}`;
};

export const Permissions = {
  DASHBOARD: {
    READ: buildPermission(PermissionModule.DASHBOARD, PermissionAction.READ),
  },
  ENQUIRY: {
    CREATE: buildPermission(PermissionModule.ENQUIRY, PermissionAction.CREATE),
    READ: buildPermission(PermissionModule.ENQUIRY, PermissionAction.READ),
    UPDATE: buildPermission(PermissionModule.ENQUIRY, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.ENQUIRY, PermissionAction.DELETE),
  },

  SITE_VISIT: {
    CREATE: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.UPDATE,
    ),
    READ: buildPermission(PermissionModule.SITE_VISIT, PermissionAction.READ),
    DELETE: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.DELETE,
    ),
    START: buildPermission(PermissionModule.SITE_VISIT, PermissionAction.START),
    COMPLETE: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.COMPLETE,
    ),
    CANCEL: buildPermission(
      PermissionModule.SITE_VISIT,
      PermissionAction.CANCEL,
    ),
  },
  WORK_ORDER: {
    READ: buildPermission(PermissionModule.WORK_ORDER, PermissionAction.READ),
    CREATE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.DELETE,
    ),
    SCHEDULE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.SCHEDULE,
    ),
    START: buildPermission(PermissionModule.WORK_ORDER, PermissionAction.START),
    HOLD: buildPermission(PermissionModule.WORK_ORDER, PermissionAction.HOLD),
    RESUME: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.RESUME,
    ),
    COMPLETE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.COMPLETE,
    ),
    CLOSE: buildPermission(PermissionModule.WORK_ORDER, PermissionAction.CLOSE),
    CANCEL: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.CANCEL,
    ),
    APPROVE: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.APPROVE,
    ),
    REJECT: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.REJECT,
    ),
    SUBMIT_BILLING: buildPermission(
      PermissionModule.WORK_ORDER,
      PermissionAction.SUBMIT_BILLING,
    ),
  },
  PETTY_WORK: {
    READ: buildPermission(PermissionModule.PETTY_WORK, PermissionAction.READ),
    CREATE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.DELETE,
    ),
    CANCEL: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.CANCEL,
    ),
    START: buildPermission(PermissionModule.PETTY_WORK, PermissionAction.START),
    HOLD: buildPermission(PermissionModule.PETTY_WORK, PermissionAction.HOLD),
    RESUME: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.RESUME,
    ),
    /** Submit completion — IN_PROGRESS → PENDING_COMPLETION_APPROVAL. */
    COMPLETE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.COMPLETE,
    ),
    /** Approve for billing — PENDING_COMPLETION_APPROVAL → READY_FOR_BILLING. */
    APPROVE: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.APPROVE,
    ),
    /** Reject completion — PENDING_COMPLETION_APPROVAL → IN_PROGRESS. */
    REJECT: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.REJECT,
    ),
    /** Submit billing — READY_FOR_BILLING → COMPLETED. */
    SUBMIT_BILLING: buildPermission(
      PermissionModule.PETTY_WORK,
      PermissionAction.SUBMIT_BILLING,
    ),
  },
  AMC_VISIT: {
    CREATE: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(PermissionModule.AMC_VISIT, PermissionAction.READ),
    UPDATE: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.DELETE,
    ),
    COMPLETE: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.COMPLETE,
    ),
    CANCEL: buildPermission(
      PermissionModule.AMC_VISIT,
      PermissionAction.CANCEL,
    ),
  },

  AMC: {
    CREATE: buildPermission(PermissionModule.AMC, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.AMC, PermissionAction.UPDATE),
    READ: buildPermission(PermissionModule.AMC, PermissionAction.READ),
    DELETE: buildPermission(PermissionModule.AMC, PermissionAction.DELETE),
    READY_FOR_ACTIVATION: buildPermission(
      PermissionModule.AMC,
      PermissionAction.READY_FOR_ACTIVATION,
    ),
    CANCEL: buildPermission(PermissionModule.AMC, PermissionAction.CANCEL),
    /** Approve completion — PENDING_COMPLETION_APPROVAL → COMPLETED. */
    COMPLETE: buildPermission(PermissionModule.AMC, PermissionAction.COMPLETE),
    /** Reject completion — PENDING_COMPLETION_APPROVAL → ACTIVE. */
    REJECT: buildPermission(PermissionModule.AMC, PermissionAction.REJECT),
  },

  EMPLOYEE_ATTENDANCE: {
    CREATE: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.UPDATE,
    ),
    READ: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.READ,
    ),
    DELETE: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.DELETE,
    ),
    /** Close a calendar day: fill missing rows from approved leave vs absent, then lock. */
    FINALIZE: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.FINALIZE,
    ),
    CLOCK_IN: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.CLOCK_IN,
    ),
    CLOCK_OUT: buildPermission(
      PermissionModule.EMPLOYEE_ATTENDANCE,
      PermissionAction.CLOCK_OUT,
    ),
  },

  EMPLOYEE: {
    CREATE: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.CREATE),
    READ: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.READ),
    READ_BASIC: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.READ_BASIC),
    UPDATE: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.EMPLOYEE, PermissionAction.DELETE),
  },
  EMPLOYEE_SALARY_STRUCTURE: {
    CREATE: buildPermission(
      PermissionModule.EMPLOYEE_SALARY_STRUCTURE,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(
      PermissionModule.EMPLOYEE_SALARY_STRUCTURE,
      PermissionAction.READ,
    ),
    UPDATE: buildPermission(
      PermissionModule.EMPLOYEE_SALARY_STRUCTURE,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.EMPLOYEE_SALARY_STRUCTURE,
      PermissionAction.DELETE,
    ),
  },

  LEAVE_APPLICATION: {
    READ: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.DELETE,
    ),
    APPROVE: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.APPROVE,
    ),
    REJECT: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.REJECT,
    ),
    CANCEL: buildPermission(
      PermissionModule.LEAVE_APPLICATION,
      PermissionAction.CANCEL,
    ),
  },

  HOLIDAY: {
    READ: buildPermission(PermissionModule.HOLIDAY, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.HOLIDAY, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.HOLIDAY, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.HOLIDAY, PermissionAction.DELETE),
  },

  LEAVE_POLICY: {
    READ: buildPermission(PermissionModule.LEAVE_POLICY, PermissionAction.READ),
    CREATE: buildPermission(
      PermissionModule.LEAVE_POLICY,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.LEAVE_POLICY,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.LEAVE_POLICY,
      PermissionAction.DELETE,
    ),
  },

  USER: {
    CREATE: buildPermission(PermissionModule.USER, PermissionAction.CREATE),
    READ: buildPermission(PermissionModule.USER, PermissionAction.READ),
    UPDATE: buildPermission(PermissionModule.USER, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.USER, PermissionAction.DELETE),
  },

  ROLES_AND_PERMISSIONS: {
    CREATE: buildPermission(
      PermissionModule.ROLES_AND_PERMISSIONS,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(
      PermissionModule.ROLES_AND_PERMISSIONS,
      PermissionAction.READ,
    ),
    UPDATE: buildPermission(
      PermissionModule.ROLES_AND_PERMISSIONS,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.ROLES_AND_PERMISSIONS,
      PermissionAction.DELETE,
    ),
  },

  CLIENT: {
    READ: buildPermission(PermissionModule.CLIENT, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.CLIENT, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.CLIENT, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.CLIENT, PermissionAction.DELETE),
  },

  QUOTATION: {
    READ: buildPermission(PermissionModule.QUOTATION, PermissionAction.READ),
    CREATE: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.DELETE,
    ),
    SEND: buildPermission(PermissionModule.QUOTATION, PermissionAction.SEND),
    ACCEPT: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.ACCEPT,
    ),
    REJECT: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.REJECT,
    ),
    CANCEL: buildPermission(
      PermissionModule.QUOTATION,
      PermissionAction.CANCEL,
    ),
  },

  PRODUCT: {
    READ: buildPermission(PermissionModule.PRODUCT, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.PRODUCT, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.PRODUCT, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.PRODUCT, PermissionAction.DELETE),
  },

  CLIENT_ADVANCE: {
    READ: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.DELETE),
    CANCEL: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.CANCEL),
    /** DRAFT → PROCESSED (attach the Payment). */
    RECEIVE: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.RECEIVE),
    /** PROCESSED → REFUNDED (full remaining balance only). */
    REFUND: buildPermission(PermissionModule.CLIENT_ADVANCE, PermissionAction.REFUND),
  },

  CLIENT_ADVANCE_SETTLEMENT: {
    READ: buildPermission(
      PermissionModule.CLIENT_ADVANCE_SETTLEMENT,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.CLIENT_ADVANCE_SETTLEMENT,
      PermissionAction.CREATE,
    ),
  },

  EMPLOYEE_ADVANCE: {
    READ: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.DELETE),
    CANCEL: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.CANCEL),
    /** DRAFT → PROCESSED (attach the Payment). */
    DISBURSE: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.DISBURSE),
    /**
     * PROCESSED → REFUNDED (full remaining balance only). Not seeded to any role yet —
     * super-admin-only for now, matching the existing CLIENT_ADVANCE.REFUND precedent.
     */
    REFUND: buildPermission(PermissionModule.EMPLOYEE_ADVANCE, PermissionAction.REFUND),
  },

  EMPLOYEE_ADVANCE_SETTLEMENT: {
    READ: buildPermission(
      PermissionModule.EMPLOYEE_ADVANCE_SETTLEMENT,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.EMPLOYEE_ADVANCE_SETTLEMENT,
      PermissionAction.CREATE,
    ),
  },

  PAYROLL: {
    READ: buildPermission(PermissionModule.PAYROLL, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.PAYROLL, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.PAYROLL, PermissionAction.UPDATE),
    FINALIZE: buildPermission(
      PermissionModule.PAYROLL,
      PermissionAction.FINALIZE,
    ),
    CANCEL: buildPermission(PermissionModule.PAYROLL, PermissionAction.CANCEL),
    DELETE: buildPermission(PermissionModule.PAYROLL, PermissionAction.DELETE),
    /** Pay out a single payroll item's salary (creates the Payment). */
    DISBURSE: buildPermission(
      PermissionModule.PAYROLL,
      PermissionAction.DISBURSE,
    ),
  },

  MATERIAL_REQUEST: {
    READ: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.DELETE,
    ),

    APPROVE: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.APPROVE,
    ),
    REJECT: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.REJECT,
    ),
    CANCEL: buildPermission(
      PermissionModule.MATERIAL_REQUEST,
      PermissionAction.CANCEL,
    ),
  },

  STOCK_MOVEMENT: {
    READ: buildPermission(
      PermissionModule.STOCK_MOVEMENT,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.STOCK_MOVEMENT,
      PermissionAction.CREATE,
    ),
  },

  STOCK_LOCATION: {
    READ: buildPermission(
      PermissionModule.STOCK_LOCATION,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.STOCK_LOCATION,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.STOCK_LOCATION,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.STOCK_LOCATION,
      PermissionAction.DELETE,
    ),
  },

  MATERIAL_ISSUE: {
    READ: buildPermission(
      PermissionModule.MATERIAL_ISSUE,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.MATERIAL_ISSUE,
      PermissionAction.CREATE,
    ),
  },

  INVOICE: {
    READ: buildPermission(PermissionModule.INVOICE, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.INVOICE, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.INVOICE, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.INVOICE, PermissionAction.DELETE),
    /** Email invoice PDF to client (`POST .../send` and `.../resend`) */
    SEND: buildPermission(PermissionModule.INVOICE, PermissionAction.SEND),
  },

  PAYMENT: {
    READ: buildPermission(PermissionModule.PAYMENT, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.PAYMENT, PermissionAction.CREATE),
    CANCEL: buildPermission(PermissionModule.PAYMENT, PermissionAction.CANCEL),
  },

  SUPPLIER: {
    READ: buildPermission(PermissionModule.SUPPLIER, PermissionAction.READ),
    CREATE: buildPermission(PermissionModule.SUPPLIER, PermissionAction.CREATE),
    UPDATE: buildPermission(PermissionModule.SUPPLIER, PermissionAction.UPDATE),
    DELETE: buildPermission(PermissionModule.SUPPLIER, PermissionAction.DELETE),
  },

  PURCHASE_ORDER: {
    READ: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.UPDATE,
    ),
    APPROVE: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.APPROVE,
    ),
    CANCEL: buildPermission(
      PermissionModule.PURCHASE_ORDER,
      PermissionAction.CANCEL,
    ),
  },

  GOODS_RECEIPT: {
    READ: buildPermission(
      PermissionModule.GOODS_RECEIPT,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.GOODS_RECEIPT,
      PermissionAction.CREATE,
    ),
  },

  EMPLOYMENT_CATEGORY: {
    READ: buildPermission(
      PermissionModule.EMPLOYMENT_CATEGORY,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.EMPLOYMENT_CATEGORY,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.EMPLOYMENT_CATEGORY,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.EMPLOYMENT_CATEGORY,
      PermissionAction.DELETE,
    ),
  },

  DEPARTMENT: {
    CREATE: buildPermission(
      PermissionModule.DEPARTMENT,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(PermissionModule.DEPARTMENT, PermissionAction.READ),
    UPDATE: buildPermission(
      PermissionModule.DEPARTMENT,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.DEPARTMENT,
      PermissionAction.DELETE,
    ),
  },

  DESIGNATION: {
    CREATE: buildPermission(
      PermissionModule.DESIGNATION,
      PermissionAction.CREATE,
    ),
    READ: buildPermission(PermissionModule.DESIGNATION, PermissionAction.READ),
    UPDATE: buildPermission(
      PermissionModule.DESIGNATION,
      PermissionAction.UPDATE,
    ),
    DELETE: buildPermission(
      PermissionModule.DESIGNATION,
      PermissionAction.DELETE,
    ),
  },

  WORK_ASSIGNMENT: {
    READ: buildPermission(
      PermissionModule.WORK_ASSIGNMENT,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.WORK_ASSIGNMENT,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.WORK_ASSIGNMENT,
      PermissionAction.UPDATE,
    ),
  },

  SETTINGS: {
    READ: buildPermission(PermissionModule.SETTINGS, PermissionAction.READ),
    UPDATE: buildPermission(PermissionModule.SETTINGS, PermissionAction.UPDATE),
  },

  PAYMENT_EXPECTATION: {
    READ: buildPermission(
      PermissionModule.PAYMENT_EXPECTATION,
      PermissionAction.READ,
    ),
    CREATE: buildPermission(
      PermissionModule.PAYMENT_EXPECTATION,
      PermissionAction.CREATE,
    ),
    UPDATE: buildPermission(
      PermissionModule.PAYMENT_EXPECTATION,
      PermissionAction.UPDATE,
    ),
    CANCEL: buildPermission(
      PermissionModule.PAYMENT_EXPECTATION,
      PermissionAction.CANCEL,
    ),
  },
  ACTIVITY_LOG: {
    READ: buildPermission(PermissionModule.ACTIVITY_LOG, PermissionAction.READ),
  },
} as const;

export function getAllRegisteredPermissions(): string[] {
  const list: string[] = [];
  Object.values(Permissions).forEach((actions) => {
    Object.values(actions).forEach((code) => list.push(code));
  });
  return list;
}

export const REGISTERED_PERMISSIONS = getAllRegisteredPermissions();
